import { toPng } from 'html-to-image';
import JSZip from 'jszip';
import { generateBlankTemplate } from '../../../pages/Editor/lib/blankTemplate';
const SLIDE_EMU = {
    width: 9144000,
    height: 6858000
};
const SLIDE_MARGIN_EMU = 457200; // 0.5 in
const WORD_MAX_EMU = {
    width: 5943600,
    height: 8229600
}; // ~6.5 in × 9 in
const PPTX_MIME = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
export function fitContain(intrinsic, box, margin) {
    const maxW = Math.max(1, box.width - 2 * margin);
    const maxH = Math.max(1, box.height - 2 * margin);
    const iw = Math.max(1, intrinsic.width);
    const ih = Math.max(1, intrinsic.height);
    const scale = Math.min(maxW / iw, maxH / ih);
    const cx = Math.round(iw * scale);
    const cy = Math.round(ih * scale);
    return {
        x: Math.round((box.width - cx) / 2),
        y: Math.round((box.height - cy) / 2),
        cx,
        cy
    };
}
export function pngBytesFromDataUrl(dataUrl) {
    const comma = dataUrl.indexOf(',');
    if (comma < 0 || !dataUrl.startsWith('data:image/png')) {
        throw new Error('expected a PNG data URL');
    }
    const binary = atob(dataUrl.slice(comma + 1));
    const out = new Uint8Array(binary.length);
    for(let i = 0; i < binary.length; i++){
        out[i] = binary.charCodeAt(i);
    }
    return out;
}
export function downloadBytes(bytes, filename, mime) {
    const blob = new Blob([
        bytes
    ], {
        type: mime
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}
function pptxPictureXml(x, y, cx, cy) {
    return [
        '<p:pic>',
        '<p:nvPicPr>',
        '<p:cNvPr id="2" name="Diagram"/>',
        '<p:cNvPicPr><a:picLocks noChangeAspect="1"/></p:cNvPicPr>',
        '<p:nvPr/>',
        '</p:nvPicPr>',
        '<p:blipFill>',
        '<a:blip r:embed="rId2"/>',
        '<a:stretch><a:fillRect/></a:stretch>',
        '</p:blipFill>',
        '<p:spPr>',
        `<a:xfrm><a:off x="${x}" y="${y}"/><a:ext cx="${cx}" cy="${cy}"/></a:xfrm>`,
        '<a:prstGeom prst="rect"><a:avLst/></a:prstGeom>',
        '</p:spPr>',
        '</p:pic>'
    ].join('');
}
export async function buildMermaidPptx(png, intrinsic) {
    const zip = await JSZip.loadAsync(await generateBlankTemplate('pptx'));
    zip.file('ppt/media/image1.png', png);
    const contentTypes = await zip.file('[Content_Types].xml')?.async('string');
    if (!contentTypes) {
        throw new Error('blank pptx missing [Content_Types].xml');
    }
    zip.file('[Content_Types].xml', contentTypes.replace('<Default Extension="xml" ContentType="application/xml"/>', '<Default Extension="xml" ContentType="application/xml"/><Default Extension="png" ContentType="image/png"/>'));
    const relsPath = 'ppt/slides/_rels/slide1.xml.rels';
    const rels = await zip.file(relsPath)?.async('string');
    if (!rels) {
        throw new Error('blank pptx missing slide rels');
    }
    zip.file(relsPath, rels.replace('</Relationships>', '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="../media/image1.png"/></Relationships>'));
    const { x, y, cx, cy } = fitContain(intrinsic, SLIDE_EMU, SLIDE_MARGIN_EMU);
    const slidePath = 'ppt/slides/slide1.xml';
    const slide = await zip.file(slidePath)?.async('string');
    if (!slide) {
        throw new Error('blank pptx missing slide1.xml');
    }
    zip.file(slidePath, slide.replace('<p:grpSpPr/>', `<p:grpSpPr/>${pptxPictureXml(x, y, cx, cy)}`));
    return zip.generateAsync({
        type: 'uint8array',
        compression: 'DEFLATE'
    });
}
export async function buildMermaidDocx(png, intrinsic) {
    const zip = await JSZip.loadAsync(await generateBlankTemplate('docx'));
    zip.file('word/media/image1.png', png);
    const contentTypes = await zip.file('[Content_Types].xml')?.async('string');
    if (!contentTypes) {
        throw new Error('blank docx missing [Content_Types].xml');
    }
    zip.file('[Content_Types].xml', contentTypes.replace('<Default Extension="xml" ContentType="application/xml"/>', '<Default Extension="xml" ContentType="application/xml"/><Default Extension="png" ContentType="image/png"/>'));
    zip.file('word/_rels/document.xml.rels', [
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="media/image1.png"/>',
        '</Relationships>'
    ].join(''));
    const { cx, cy } = fitContain(intrinsic, WORD_MAX_EMU, 0);
    zip.file('word/document.xml', [
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
        '<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"',
        ' xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"',
        ' xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing"',
        ' xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"',
        ' xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture">',
        '<w:body><w:p><w:r><w:drawing>',
        '<wp:inline distT="0" distB="0" distL="0" distR="0">',
        `<wp:extent cx="${cx}" cy="${cy}"/>`,
        '<wp:docPr id="1" name="Diagram"/>',
        '<wp:cNvGraphicFramePr><a:graphicFrameLocks noChangeAspect="1"/></wp:cNvGraphicFramePr>',
        '<a:graphic><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture">',
        '<pic:pic>',
        '<pic:nvPicPr><pic:cNvPr id="0" name="diagram.png"/><pic:cNvPicPr/></pic:nvPicPr>',
        '<pic:blipFill><a:blip r:embed="rId1"/><a:stretch><a:fillRect/></a:stretch></pic:blipFill>',
        '<pic:spPr>',
        `<a:xfrm><a:off x="0" y="0"/><a:ext cx="${cx}" cy="${cy}"/></a:xfrm>`,
        '<a:prstGeom prst="rect"><a:avLst/></a:prstGeom>',
        '</pic:spPr>',
        '</pic:pic>',
        '</a:graphicData></a:graphic>',
        '</wp:inline>',
        '</w:drawing></w:r></w:p></w:body></w:document>'
    ].join(''));
    return zip.generateAsync({
        type: 'uint8array',
        compression: 'DEFLATE'
    });
}
export async function exportMermaidRaster(svg, format, options) {
    const dataUrl = await toPng(svg, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: options.backgroundColor
    });
    if (format === 'png') {
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = 'diagram.png';
        a.click();
        return;
    }
    const png = pngBytesFromDataUrl(dataUrl);
    if (format === 'pptx') {
        downloadBytes(await buildMermaidPptx(png, options.size), 'diagram.pptx', PPTX_MIME);
        return;
    }
    downloadBytes(await buildMermaidDocx(png, options.size), 'diagram.docx', DOCX_MIME);
}
